import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Grid,
  CircularProgress,
  Alert,
  Divider,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Pagination,
  Skeleton,
  Tooltip,
  IconButton,
  Collapse,
  Paper,
  Stack
} from '@mui/material';
import {
  Receipt as ReceiptIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  FilterList as FilterListIcon,
  Sort as SortIcon,
  ClearAll as ClearAllIcon,
  CalendarMonth as CalendarIcon,
  AccountBalance as AccountBalanceIcon,
  TrendingUp as TrendingUpIcon,
  HourglassEmpty as HourglassIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  Payment as PaymentIcon,
  Send as SendIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import ownerCommissionService from '../../services/ownerCommissionService';

// ─────────────────────────────────────────────
// Config statuts
// ─────────────────────────────────────────────
const STATUS_CONFIG = {
  draft:              { color: 'default',  icon: <ScheduleIcon fontSize="small" />,  muiColor: '#9e9e9e', bg: '#f5f5f5' },
  pending:            { color: 'warning',  icon: <HourglassIcon fontSize="small" />, muiColor: '#f57c00', bg: '#fff8e1' },
  link_sent:          { color: 'info',     icon: <SendIcon fontSize="small" />,      muiColor: '#0288d1', bg: '#e1f5fe' },
  payment_initiated:  { color: 'secondary',icon: <PaymentIcon fontSize="small" />,   muiColor: '#7b1fa2', bg: '#f3e5f5' },
  paid:               { color: 'success',  icon: <CheckCircleIcon fontSize="small" />, muiColor: '#2e7d32', bg: '#e8f5e9' },
  overdue:            { color: 'error',    icon: <WarningIcon fontSize="small" />,   muiColor: '#c62828', bg: '#ffebee' },
  cancelled:          { color: 'default',  icon: <CancelIcon fontSize="small" />,    muiColor: '#757575', bg: '#eeeeee' },
  failed:             { color: 'error',    icon: <ErrorIcon fontSize="small" />,     muiColor: '#b71c1c', bg: '#ffcdd2' },
};

const SORT_OPTIONS = [
  { value: 'created_at|DESC', label: 'invoices.sort.newestFirst' },
  { value: 'created_at|ASC',  label: 'invoices.sort.oldestFirst' },
  { value: 'total_amount|DESC', label: 'invoices.sort.amountDesc' },
  { value: 'total_amount|ASC',  label: 'invoices.sort.amountAsc' },
  { value: 'period_start|DESC', label: 'invoices.sort.periodDesc' },
  { value: 'due_date|ASC',      label: 'invoices.sort.dueDateAsc' },
];

const ALL_STATUSES = ['draft', 'pending', 'link_sent', 'payment_initiated', 'paid', 'overdue', 'cancelled', 'failed'];
const PAGE_SIZE = 12;

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────
const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatAmount = (amount, currency = 'EUR') => {
  if (amount == null) return '—';
  try {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency }).format(amount);
  } catch {
    return `${amount} ${currency}`;
  }
};

// ─────────────────────────────────────────────
// Composant InvoiceCard
// ─────────────────────────────────────────────
const InvoiceCard = ({ invoice, onDownloadPdf, downloadingId, t }) => {
  const navigate = useNavigate();
  const cfg = STATUS_CONFIG[invoice.status] || STATUS_CONFIG.pending;
  const isDownloading = downloadingId === invoice.id;

  const canPay = ['pending', 'link_sent', 'overdue'].includes(invoice.status);

  const handleViewDetail = () => {
    navigate(`/owner/invoices/${invoice.id}`);
  };

  return (
    <Card
      elevation={2}
      sx={{
        borderRadius: 3,
        border: `1px solid ${cfg.muiColor}30`,
        transition: 'box-shadow 0.2s, transform 0.15s',
        '&:hover': { boxShadow: 6, transform: 'translateY(-2px)' },
        display: 'flex',
        flexDirection: 'column',
        height: '100%'
      }}
    >
      {/* Header coloré */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${cfg.muiColor}18 0%, ${cfg.bg} 100%)`,
          borderBottom: `2px solid ${cfg.muiColor}40`,
          px: 2.5,
          pt: 2,
          pb: 1.5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start'
        }}
      >
        <Box>
          <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase' }}>
            {t('invoices.invoiceNumber', 'Facture')}
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a2e', fontSize: '1rem', lineHeight: 1.2 }}>
            {invoice.invoice_number || t('invoices.noInvoiceNumber', 'N/A')}
          </Typography>
        </Box>
        <Chip
          icon={cfg.icon}
          label={t(`invoices.status.${invoice.status}`, invoice.status)}
          color={cfg.color}
          size="small"
          sx={{ fontWeight: 600, borderRadius: 2 }}
        />
      </Box>

      <CardContent sx={{ flex: 1, pt: 2, pb: 1 }}>
        {/* Montant */}
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 800, color: cfg.muiColor }}>
            {formatAmount(invoice.total_amount, invoice.currency)}
          </Typography>
          {invoice.payins_count > 0 && (
            <Typography variant="caption" sx={{ color: '#888' }}>
              {t('invoices.payinsCount', { count: invoice.payins_count })}
            </Typography>
          )}
        </Box>

        <Divider sx={{ mb: 1.5 }} />

        {/* Période */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <CalendarIcon fontSize="small" sx={{ color: '#888', flexShrink: 0 }} />
          <Box>
            <Typography variant="caption" sx={{ color: '#888', display: 'block', lineHeight: 1.1 }}>
              {t('invoices.period', 'Période')}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
              {formatDate(invoice.period_start)} – {formatDate(invoice.period_end)}
            </Typography>
          </Box>
        </Box>

        {/* Date d'échéance */}
        {invoice.due_date && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <WarningIcon fontSize="small" sx={{ color: invoice.status === 'overdue' ? '#c62828' : '#888', flexShrink: 0 }} />
            <Box>
              <Typography variant="caption" sx={{ color: '#888', display: 'block', lineHeight: 1.1 }}>
                {t('invoices.dueDate', 'Échéance')}
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, fontSize: '0.8rem', color: invoice.status === 'overdue' ? '#c62828' : 'inherit' }}
              >
                {formatDate(invoice.due_date)}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Date de paiement */}
        {invoice.paid_date && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleIcon fontSize="small" sx={{ color: '#2e7d32', flexShrink: 0 }} />
            <Box>
              <Typography variant="caption" sx={{ color: '#888', display: 'block', lineHeight: 1.1 }}>
                {t('invoices.paidDate', 'Payé le')}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#2e7d32' }}>
                {formatDate(invoice.paid_date)}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Lien de paiement disponible */}
        {canPay && invoice.stripe_payment_link_url && (
          <Box sx={{ mt: 1.5 }}>
            <Button
              variant="contained"
              color="warning"
              size="small"
              fullWidth
              href={invoice.stripe_payment_link_url}
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<PaymentIcon />}
              sx={{ borderRadius: 2, fontWeight: 700 }}
            >
              {t('invoices.payNow', 'Payer maintenant')}
            </Button>
          </Box>
        )}
      </CardContent>

      <CardActions sx={{ px: 2, pb: 2, pt: 0, gap: 1, flexWrap: 'wrap' }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<VisibilityIcon />}
          onClick={handleViewDetail}
          sx={{ borderRadius: 2, flex: 1, minWidth: 0 }}
        >
          {t('invoices.viewDetails', 'Détails')}
        </Button>
        <Tooltip title={t('invoices.downloadPdf', 'Télécharger PDF')}>
          <span>
            <Button
              variant="outlined"
              size="small"
              color="secondary"
              startIcon={isDownloading ? <CircularProgress size={14} /> : <DownloadIcon />}
              onClick={() => onDownloadPdf(invoice)}
              disabled={isDownloading}
              sx={{ borderRadius: 2, flex: 1, minWidth: 0 }}
            >
              {isDownloading ? t('invoices.downloading', '…') : 'PDF'}
            </Button>
          </span>
        </Tooltip>
      </CardActions>
    </Card>
  );
};

// ─────────────────────────────────────────────
// Composant SkeletonCard
// ─────────────────────────────────────────────
const SkeletonCard = () => (
  <Card elevation={1} sx={{ borderRadius: 3, height: '100%' }}>
    <Box sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
      <Skeleton variant="text" width="60%" height={20} />
      <Skeleton variant="text" width="80%" height={28} />
    </Box>
    <CardContent sx={{ pt: 2 }}>
      <Skeleton variant="text" width="50%" height={36} sx={{ mx: 'auto', mb: 1 }} />
      <Skeleton variant="rectangular" height={1} sx={{ mb: 1.5 }} />
      <Skeleton variant="text" width="70%" />
      <Skeleton variant="text" width="60%" />
    </CardContent>
    <CardActions sx={{ px: 2, pb: 2 }}>
      <Skeleton variant="rectangular" height={32} sx={{ flex: 1, borderRadius: 2 }} />
      <Skeleton variant="rectangular" height={32} sx={{ flex: 1, borderRadius: 2, ml: 1 }} />
    </CardActions>
  </Card>
);

// ─────────────────────────────────────────────
// Composant StatsBar
// ─────────────────────────────────────────────
const StatsBar = ({ stats, t }) => {
  if (!stats || stats.length === 0) return null;

  const statsByStatus = stats.reduce((acc, s) => {
    acc[s.status] = s;
    return acc;
  }, {});

  const totalCount = stats.reduce((sum, s) => sum + parseInt(s.count || 0), 0);
  const totalAmount = stats.reduce((sum, s) => sum + parseFloat(s.total_amount || 0), 0);
  const pendingAmount = ['pending', 'overdue', 'link_sent'].reduce(
    (sum, status) => sum + parseFloat(statsByStatus[status]?.total_amount || 0), 0
  );
  const paidAmount = parseFloat(statsByStatus['paid']?.total_amount || 0);

  return (
    <Paper elevation={0} sx={{ borderRadius: 3, p: 2.5, mb: 3, border: '1px solid #e0e0e0', background: '#fafafa' }}>
      <Grid container spacing={2} justifyContent="center">
        <Grid item xs={6} sm={3}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#1a1a2e' }}>{totalCount}</Typography>
            <Typography variant="caption" sx={{ color: '#666' }}>{t('invoices.stats.total', 'Total factures')}</Typography>
          </Box>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#2e7d32' }}>{formatAmount(paidAmount)}</Typography>
            <Typography variant="caption" sx={{ color: '#666' }}>{t('invoices.stats.paid', 'Montant payé')}</Typography>
          </Box>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#f57c00' }}>{formatAmount(pendingAmount)}</Typography>
            <Typography variant="caption" sx={{ color: '#666' }}>{t('invoices.stats.pending', 'En attente')}</Typography>
          </Box>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#1976d2' }}>{formatAmount(totalAmount)}</Typography>
            <Typography variant="caption" sx={{ color: '#666' }}>{t('invoices.stats.totalAmount', 'Total général')}</Typography>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

// ─────────────────────────────────────────────
// Page principale
// ─────────────────────────────────────────────
const OwnerInvoicesPage = () => {
  const { t } = useTranslation('financial');
  const navigate = useNavigate();

  // États
  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [downloadingId, setDownloadingId] = useState(null);

  // Filtres
  const [showFilters, setShowFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortValue, setSortValue] = useState('created_at|DESC');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // ─── Chargement des factures ───
  const fetchInvoices = useCallback(async (currentPage = 1) => {
    setLoading(true);
    setError(null);
    try {
      const [sort_by, sort_order] = sortValue.split('|');
      const params = {
        page: currentPage,
        limit: PAGE_SIZE,
        sort_by,
        sort_order,
      };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;

      const data = await ownerCommissionService.getOwnerInvoices(params);
      setInvoices(data.invoices || []);
      setStats(data.stats || []);
      setTotalPages(data.pagination?.totalPages || 1);
    } catch (err) {
      console.error('Erreur chargement factures:', err);
      setError(t('invoices.error', 'Impossible de charger vos factures. Veuillez réessayer.'));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, sortValue, dateFrom, dateTo, t]);

  useEffect(() => {
    setPage(1);
    fetchInvoices(1);
  }, [statusFilter, sortValue, dateFrom, dateTo]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (page > 1) fetchInvoices(page);
  }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Téléchargement PDF ───
  const handleDownloadPdf = async (invoice) => {
    if (downloadingId) return;
    setDownloadingId(invoice.id);
    try {
      const blob = await ownerCommissionService.downloadInvoicePdf(invoice.id);
      if (!blob || blob.size === 0) throw new Error('PDF vide');
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `facture-commission-${invoice.invoice_number || invoice.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Erreur téléchargement PDF:', err);
      alert(t('invoices.pdfError', 'Erreur lors du téléchargement du PDF. Veuillez réessayer.'));
    } finally {
      setDownloadingId(null);
    }
  };

  // ─── Reset filtres ───
  const handleClearFilters = () => {
    setStatusFilter('all');
    setSortValue('created_at|DESC');
    setDateFrom('');
    setDateTo('');
    setPage(1);
  };

  const hasActiveFilters = statusFilter !== 'all' || sortValue !== 'created_at|DESC' || dateFrom || dateTo;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* ── En-tête gradient ── */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 4,
          p: 3,
          borderRadius: 2,
          background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
          color: 'white',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
            {t('invoices.title', 'Mes Factures')}
          </Typography>
          <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
            {t('invoices.subtitle', 'Toutes vos factures de commission, tous statuts confondus')}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AccountBalanceIcon />}
          onClick={() => navigate('/financial-owner')}
          sx={{
            bgcolor: 'rgba(255,255,255,0.2)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.3)',
            color: 'white',
            fontWeight: 'medium',
            px: 3,
            py: 1.5,
            '&:hover': {
              bgcolor: 'rgba(255,255,255,0.3)',
              transform: 'translateY(-2px)',
              boxShadow: '0 6px 20px rgba(0,0,0,0.2)'
            },
            transition: 'all 0.3s ease'
          }}
        >
          {t('invoices.financialCenter', 'Centre Financier')}
        </Button>
      </Box>

      {/* ── Statistiques ── */}
      {!loading && stats.length > 0 && (
        <StatsBar stats={stats} t={t} />
      )}
      {loading && (
        <Paper elevation={0} sx={{ borderRadius: 3, p: 2.5, mb: 3, border: '1px solid #e0e0e0' }}>
          <Grid container spacing={2}>
            {[1,2,3,4].map(i => (
              <Grid item xs={6} sm={3} key={i}>
                <Skeleton variant="text" width="50%" height={36} sx={{ mx: 'auto' }} />
                <Skeleton variant="text" width="70%" height={18} sx={{ mx: 'auto' }} />
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      {/* ── Barre de filtres ── */}
      <Paper elevation={0} sx={{ borderRadius: 3, p: 2, mb: 3, border: '1px solid #e0e0e0' }}>
        {/* Ligne principale : status chips + bouton filtre/sort */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography variant="body2" sx={{ fontWeight: 700, color: '#444', mr: 0.5, flexShrink: 0 }}>
            {t('invoices.filterStatus', 'Statut :')}
          </Typography>
          {/* Chip "Tous" */}
          <Chip
            label={t('invoices.status.all', 'Tous')}
            onClick={() => { setStatusFilter('all'); setPage(1); }}
            color={statusFilter === 'all' ? 'primary' : 'default'}
            variant={statusFilter === 'all' ? 'filled' : 'outlined'}
            size="small"
            sx={{ fontWeight: 600 }}
          />
          {ALL_STATUSES.map(status => {
            const cfg = STATUS_CONFIG[status];
            return (
              <Chip
                key={status}
                label={t(`invoices.status.${status}`, status)}
                icon={cfg.icon}
                onClick={() => { setStatusFilter(status); setPage(1); }}
                color={statusFilter === status ? cfg.color : 'default'}
                variant={statusFilter === status ? 'filled' : 'outlined'}
                size="small"
                sx={{ fontWeight: 600 }}
              />
            );
          })}

          <Box sx={{ ml: 'auto', display: 'flex', gap: 1, flexShrink: 0 }}>
            <Tooltip title={t('invoices.toggleFilters', 'Filtres avancés')}>
              <IconButton
                size="small"
                onClick={() => setShowFilters(v => !v)}
                color={showFilters ? 'primary' : 'default'}
                sx={{ border: '1px solid #ddd', borderRadius: 2 }}
              >
                <FilterListIcon />
              </IconButton>
            </Tooltip>
            {hasActiveFilters && (
              <Tooltip title={t('invoices.clearFilters', 'Réinitialiser')}>
                <IconButton
                  size="small"
                  onClick={handleClearFilters}
                  color="error"
                  sx={{ border: '1px solid #ffcdd2', borderRadius: 2 }}
                >
                  <ClearAllIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        {/* Filtres avancés (date + tri) */}
        <Collapse in={showFilters}>
          <Divider sx={{ my: 1.5 }} />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <TextField
              label={t('invoices.dateFrom', 'Période du')}
              type="date"
              size="small"
              value={dateFrom}
              onChange={e => { setDateFrom(e.target.value); setPage(1); }}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 160 }}
            />
            <TextField
              label={t('invoices.dateTo', 'au')}
              type="date"
              size="small"
              value={dateTo}
              onChange={e => { setDateTo(e.target.value); setPage(1); }}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 160 }}
            />
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>{t('invoices.sortBy', 'Trier par')}</InputLabel>
              <Select
                value={sortValue}
                label={t('invoices.sortBy', 'Trier par')}
                onChange={e => { setSortValue(e.target.value); setPage(1); }}
                startAdornment={<SortIcon sx={{ mr: 1, color: '#888' }} />}
              >
                {SORT_OPTIONS.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {t(opt.label, opt.label)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Collapse>
      </Paper>

      {/* ── État d'erreur ── */}
      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* ── Grille de cartes ── */}
      {loading ? (
        <Grid container spacing={2.5}>
          {Array.from({ length: PAGE_SIZE }).map((_, i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <SkeletonCard />
            </Grid>
          ))}
        </Grid>
      ) : invoices.length === 0 ? (
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            px: 2,
            borderRadius: 4,
            border: '2px dashed #e0e0e0',
            background: '#fafafa'
          }}
        >
          <ReceiptIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
          <Typography variant="h6" sx={{ color: '#666', mb: 1 }}>
            {t('invoices.empty.title', 'Aucune facture trouvée')}
          </Typography>
          <Typography variant="body2" sx={{ color: '#999', mb: 3 }}>
            {hasActiveFilters
              ? t('invoices.empty.withFilters', 'Aucune facture ne correspond à vos filtres.')
              : t('invoices.empty.noInvoices', 'Vous n\'avez pas encore de facture de commission.')}
          </Typography>
          {hasActiveFilters && (
            <Button variant="outlined" onClick={handleClearFilters} startIcon={<ClearAllIcon />}>
              {t('invoices.clearFilters', 'Réinitialiser les filtres')}
            </Button>
          )}
        </Box>
      ) : (
        <>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ color: '#666' }}>
              {t('invoices.showingCount', { count: invoices.length, total: totalPages * PAGE_SIZE })}
            </Typography>
          </Box>
          <Grid container spacing={2.5}>
            {invoices.map(invoice => (
              <Grid item xs={12} sm={6} md={4} key={invoice.id}>
                <InvoiceCard
                  invoice={invoice}
                  onDownloadPdf={handleDownloadPdf}
                  downloadingId={downloadingId}
                  t={t}
                />
              </Grid>
            ))}
          </Grid>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, val) => { setPage(val); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                color="primary"
                shape="rounded"
                size="large"
              />
            </Box>
          )}
        </>
      )}
    </Container>
  );
};

export default OwnerInvoicesPage;
